import { Component, JSX } from "react";
import { RouteComponentProps } from "react-router-dom";

import { UserContext, UserInfo } from "@app/user";
import CreateProject from "./createProject";
import CreateProjectService from "./createProjectService";
import { Fields, RegionSetParseResults } from "./interfaces";

import getOptions from "./data/options";

import { GeographyService } from "@app/geography";
import { ListAccordion, PageSection } from "@app/shared";
import { QESAlert } from "@app/shared/qes-standards";
import { RoleRestricted, Roles } from "@app/user";
import WizardStep from "./WizardStep";
import ZipProjectService from "../zipProjectService";
import { convertCreateProjectToZipProjectRequestModel } from "../zip-projects/interfaces/createZipProjectRequestModel";
import { ResourceTagModel } from "@app/tags/import/types";


interface CreateProjectStepReviewProps extends RouteComponentProps {
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  baseUrl: string;
  progressList: string[];
  removeNonAlternateMemberships: () => void;
  currentIndex: number;
  hideStrictTimeDistance: boolean;
  isZipProject: boolean;
  hideMotiveAppropriatenessScore: boolean;
  hideCMSApplicationCredit: boolean;
  hideCareJourneyScores: boolean;
  hideClosestProviderReportOption: boolean;
  hideEnterpriseComplianceDashboard: boolean;
  hidePracAtLocCondifence: boolean;
  hideProviderClaimsActivity: boolean;
  showNetworkIntegrityDashboard: boolean;
}

interface CreateProjectStepReviewState {
  error?: Error;
  region: RegionSetParseResults;
  submitted: boolean;
}

export default class CreateProjectStepReview extends Component<CreateProjectStepReviewProps, CreateProjectStepReviewState> {
  private readonly createProjectService = new CreateProjectService();
  private readonly zipProjectService = new ZipProjectService();
  private readonly geographyService = new GeographyService();

  private filteredTags: ResourceTagModel[] = [];

  constructor(props: CreateProjectStepReviewProps) {
    super(props);

    this.state = {
      region: null,
      submitted: false,
    };
  }

  public componentDidMount() {
    if (this.props.createProjectModel.serviceAreaRegionSetId) {
      // changing this from await so the component doesn't hang while this is being processed
      this.geographyService.getParsedRegionSet(this.props.createProjectModel.serviceAreaRegionSetId).then((data) => {
        this.setState({
          region: data,
        });
      });
    }

    this.filteredTags = this.props.createProjectModel.tags?.filter(tag => tag.tagGroupName.length > 0 && tag.tagName.length > 0);
  }

  public handleFormSubmit = async (runNow: boolean): Promise<void> => {
    try {
      this.setState({
        submitted: true,
      });

      this.props.removeNonAlternateMemberships();
      const project: CreateProject = structuredClone(this.props.createProjectModel);
      if (!project.marketProviderSetId && project.useMarketProvidersForImpact) {
        project.useMarketProvidersForImpact = false;
      }

      if (!this.props.isZipProject) {
        await this.createProjectService.createProject(project, runNow);
      } else {
        await this.zipProjectService.createZipProject(convertCreateProjectToZipProjectRequestModel(project, runNow));
      }

      window.location.href = window.customUIBaseUrl + "/";
    } catch (error) {
      console.error(error);
      this.setState({
        error,
        submitted: false,
      });
    }
  }

  public render() {
    return (
      <>
        {this.state.error && (
          <PageSection>
            <div className="row">
              <div className="col-12">
                <QESAlert type="danger" classNames="m10" persist={true}>
                  <strong>Uh oh! </strong>An unexpected error occurred. If the issue persists, please contact your administrator.
                </QESAlert>
              </div>
            </div>
          </PageSection>
        )}
        <WizardStep
          {...this.props}
          heading="Create a New Project"
          subheading="Confirm / Review"
          baseUrl={this.props.baseUrl}
          prevUrl={(this.props.currentIndex - 1).toString()}
          progressList={this.props.progressList}
          onSubmitClick={(e) => this.handleFormSubmit(e)}
          isPrevDisabled={this.state.submitted}
          isNextDisabled={this.handleIsNextDisabled()}
          submitted={this.state.submitted}
        >
          <UserContext.Consumer>
            {(user) => (
              <>
                <div className="form-group row">
                  <label className="col-3 font-weight-bold">Project Name: </label>
                  <div className="col-4">{this.props.createProjectModel.name}</div>
                </div>
                <div className="form-group row">
                  <label className="col-3 font-weight-bold">Description: </label>
                  <div className="col-4">{this.props.createProjectModel.description}</div>
                </div>
                <RoleRestricted allowedRoles={[Roles.CLIENT_ADMIN]}>
                  <div className="form-group row">
                    <label className="col-3 font-weight-bold">Groups/Tags: </label>
                    <div className="col-4">
                      {this.filteredTags.length > 0 && this.filteredTags.map((tag, index) =>
                        <div key={`tagcontainer${index}`}>
                          <label className="mr-2 font-weight-bold">Group: </label>{tag.tagGroupName}
                          <label className="ml-3 mr-2 font-weight-bold">Tag Name: </label>{tag.tagName}
                        </div>
                      )}
                    </div>
                  </div>
                </RoleRestricted>
                <RoleRestricted allowedRoles={[Roles.RESOURCE_ADMIN]}>
                  <div className="form-group row">
                    {this.props.createProjectModel.isPrivate && (
                      <div className="offset-3 col-4">This is a Private Project (can be administered only by you)</div>
                    )}
                    {!this.props.createProjectModel.isPrivate && (
                      <div className="offset-3 col-4">This is a non-Private Project (can be administered by you and other users with permission to adminster non-Private projects)</div>
                    )}
                  </div>
                </RoleRestricted>
                {!this.props.isZipProject && (
                  <>
                    <div className="form-group row">
                      <label className="col-3 font-weight-bold">Accuracy Template Collection: </label>
                      <div className="col-4">{this.props.createProjectModel.accuracyTemplateCollectionDescription}</div>
                    </div>
                    <div className="form-group row">
                      <label className="col-3 font-weight-bold">Accuracy Template: </label>
                      <div className="col-4">{this.props.createProjectModel.accuracyTemplateCorrelationId}</div>
                    </div>
                    <div className="form-group row">
                      <label className="col-3 font-weight-bold">Accuracy Specialty to BDX UID Code Map: </label>
                      <div className="col-4">{this.props.createProjectModel.accuracySpecialtyToQdpCodeMapSetDescription}</div>
                    </div>
                    <div className="form-group row">
                      <label className="col-3 font-weight-bold">Inspected Insurances: </label>
                      <ul className="col-4 list-group-flush">
                        {this.props.createProjectModel.inspectedInsurancesList.map((ins) => (
                          <li key={ins.uid} className="list-group-item">{ins.planName}: {ins.uid}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
                <div className="form-group row">
                  <label className="col-3 font-weight-bold">Adequacy Template Collection: </label>
                  <div className="col-4">{this.props.createProjectModel.adequacyTemplateCollectionDescription}</div>
                </div>
                <div className="form-group row">
                  <label className="col-3 font-weight-bold">Adequacy Template: </label>
                  <div className="col-4">{this.props.createProjectModel.adequacyTemplateCorrelationId}</div>
                </div>
                {this.props.createProjectModel.memberPopDescription !== "" && (
                  <div className="form-group row">
                    <label className="col-3 font-weight-bold">Member Population: </label>
                    <div className="col-4">{this.props.createProjectModel.memberPopDescription}
                      {this.props.createProjectModel.memberSetAlias !== "" && (
                        <label className="ml-1">
                          (Alias: {this.props.createProjectModel.memberSetAlias})
                        </label>)}
                    </div>
                  </div>
                )}
                {this.props.createProjectModel.memberPopDescription2 !== "" && (
                  <div className="form-group row">
                    <label className="col-3 font-weight-bold">Second Member Population: </label>
                    <div className="col-4">{this.props.createProjectModel.memberPopDescription2}
                      {this.props.createProjectModel.memberSetAlias2 !== "" && (
                        <label className="ml-1">
                          (Alias: {this.props.createProjectModel.memberSetAlias2})
                        </label>)}
                    </div>
                  </div>
                )}
                <RoleRestricted allowedRoles={[Roles.MEMBERSHIP_RISK_USER]}>
                  {this.props.createProjectModel.membershipRiskMemberSetAlias !== "" && (
                    <div className="form-group row">
                      <label className="col-3 font-weight-bold">Risk Analysis Member Population: </label>
                      <div className="col-4">
                        {this.props.createProjectModel.membershipRiskMemberSetAlias}
                      </div>
                    </div>
                  )}
                </RoleRestricted>
                <div className="form-group row">
                  <label className="col-3 font-weight-bold">Network Providers: </label>
                  <div className="col-4">{this.props.createProjectModel.networkProviderDescription}</div>
                </div>
                {this.props.createProjectModel.prospectProviderDescription !== "" && (
                  <div className="form-group row">
                    <label className="col-3 font-weight-bold">Prospect Providers: </label>
                    <div className="col-4">{this.props.createProjectModel.prospectProviderDescription}</div>
                  </div>
                )}
                {this.props.createProjectModel.marketProviderDescription !== "" && (
                  <>
                    <div className="form-group row">
                      <label className="col-3 font-weight-bold">Market Providers: </label>
                      <div className="col-4">{this.props.createProjectModel.marketProviderDescription}</div>
                    </div>
                  </>
                )}
                {(this.props.createProjectModel.networkTaxonomy) &&
                  <>
                    {this.props.createProjectModel.networkTaxonomy.fileType &&
                      <div className="form-group row">
                        <label className="col-3 font-weight-bold">File Type:</label>
                        <div className="col-4">{this.props.createProjectModel.networkTaxonomy.fileType}</div>
                      </div>
                    }
                    {this.props.createProjectModel.networkTaxonomy.fileTypeReason &&
                      <div className="form-group row">
                        <label className="col-3 font-weight-bold">Other File Type:</label>
                        <div className="col-4">{this.props.createProjectModel.networkTaxonomy.fileTypeReason}</div>
                      </div>
                    }
                    {this.props.createProjectModel.networkTaxonomy.projectUtilizationCategory &&
                      <div className="form-group row">
                        <label className="col-3 font-weight-bold">Project Utilization Category:</label>
                        <div className="col-4">{this.props.createProjectModel.networkTaxonomy.projectUtilizationCategory}</div>
                      </div>
                    }
                    {this.props.createProjectModel.networkTaxonomy.lineOfBusiness &&
                      <div className="form-group row">
                        <label className="col-3 font-weight-bold">Line Of Business:</label>
                        <div className="col-4">{this.props.createProjectModel.networkTaxonomy.lineOfBusiness}</div>
                      </div>
                    }
                    {this.props.createProjectModel.networkTaxonomy.lineOfBusinessTypeId &&
                      <div className="form-group row">
                        <label className="col-3 font-weight-bold">Line Of Business Type:</label>
                        <div className="col-4">{this.props.createProjectModel.networkTaxonomy.lineOfBusinessType}</div>
                      </div>
                    }
                    {this.props.createProjectModel.networkTaxonomy.identifiers && this.props.createProjectModel.networkTaxonomy.identifiers.length > 0 &&
                      <div className="form-group row">
                        <label className="col-3 font-weight-bold">Identifiers:</label>
                        <div className="col-8">
                          {this.getReadableNetworkTaxonomyIdentifierResults()}
                        </div>
                      </div>
                    }
                  </>
                }
                <div className="form-group row">
                  <label className="col-3 font-weight-bold">Service Area: </label>
                  {!this.state.region ? <div className="col-4">Loading...</div> : (
                    <div className="col-8">
                      {this.getReadableServiceAreaResults()}
                    </div>
                  )}
                </div>
                {this.props.createProjectModel.accessCodeMapSetId !== null &&
                  <div className="form-group row">
                    <label className="col-3 font-weight-bold">Access Region to Classification Code Map: </label>
                    <div className="col-4">{this.props.createProjectModel.accessCodeMapSetDescription}</div>
                  </div>
                }
                {this.getOptions(user).length > 0 ? (
                  <div className="form-group row">
                    <label className="col-3 font-weight-bold mb0">Project Options: </label>
                    {this.getOptions(user).map((option, index) => <div key={option.title} className={`col-9 ${index > 0 ? "offset-3" : ""}`}>{option.title}</div>)}
                  </div>
                ) : null}
              </>
            )}
          </UserContext.Consumer>
        </WizardStep>
      </>
    );
  }

  private getOptions = (user: UserInfo) =>
    getOptions<CreateProject>(
      {
        accuracyAndAdequacy: this.props.createProjectModel.accuracyAndAdequacy,
        displayOnNetworkAccordion: this.props.createProjectModel.displayOnNetworkAccordion,
        enablePatternOfCareProvidersOption: this.props.createProjectModel.hasZipSummaries && this.props.createProjectModel.showClosestProviderReport,
        enableRegulatoryExceptionReportsOption: this.props.createProjectModel.hasZipSummaries && this.props.createProjectModel.showClosestProviderReport,
        hideCMSApplicationCredit: this.props.hideCMSApplicationCredit,
        hideCareJourneyScores: this.props.hideCareJourneyScores,
        hideClosestProviderReport: this.props.hideClosestProviderReportOption,
        hideEnterpriseComplianceDashboard: this.props.hideEnterpriseComplianceDashboard,
        hideMotiveAppropriatenessScore: this.props.hideMotiveAppropriatenessScore,
        hidePciData: !this.props.createProjectModel.adequacyTemplateCollectionId,
        hidePracAtLocCondifence: this.props.hidePracAtLocCondifence,
        hideProviderClaimsActivity: this.props.hideProviderClaimsActivity,
        hideStrictTimeDistance: this.props.hideStrictTimeDistance,
        isZipProject: this.props.isZipProject,
        showAffiliationsInsights: this.props.createProjectModel.showAffiliationsInsights,
        showNetworkIntegrityDashboard: this.props.showNetworkIntegrityDashboard,
        showPciDataChecked: this.props.createProjectModel.showPciData,
        useAccuracy: this.props.createProjectModel.accuracyTemplateCollectionId !== null,
        userRoles: user.roles
      }
    )
      .reduce((acc, current) => acc = [...acc, ...current.fields], new Array<Fields<CreateProject>>())
      .filter((option) => this.props.createProjectModel[option.key as keyof CreateProject]);

  private handleIsNextDisabled = () => {
    return !(
      this.props.completionTracker.filter((_, index) => (this.props.currentIndex - 1) > index).every(step => step) &&
      !this.state.submitted
    );
  }

  private getReadableServiceAreaResults = () => {
    const readableResults: Array<JSX.Element[] | JSX.Element> = [];
    const results = this.state.region;

    readableResults.push(results.cbsas.map((cbsaName: string) => (<ListAccordion key={cbsaName} titleElement={<span>{cbsaName} (CBSA)</span>} />)));

    readableResults.push(results.states.map((state) => (
      <ListAccordion key={state.name} titleElement={<span>{state.name}</span>}>
        {state.counties.map((county) => (
          <ListAccordion key={county.name} titleElement={<span>{`${county.name} (${this.props.createProjectModel.countyStatuses.find((item) => item.countySSA === county.countySSA).status})`}</span>}>
            {county.countyZips.map((countyZip: string) => (
              <li key={countyZip}>{countyZip}</li>
            ))}
          </ListAccordion>
        ))}
        {state.zips.map((zip) => (
          <li key={zip}>{zip}</li>
        ))}
      </ListAccordion>
    )));

    return (<ul>{readableResults}</ul>);
  }

  private getReadableNetworkTaxonomyIdentifierResults = () => {
    return (
      <ul>
        <ListAccordion titleElement={<span>Identifiers</span>}>
          {this.props.createProjectModel.networkTaxonomy.identifiers.map((identifier) => (
            <ListAccordion key={identifier.name} titleElement={<span>{`${identifier.name}: ${identifier.value}`}</span>} />
          ))}
        </ListAccordion>
      </ul>
    )
  }
}
